import { resolveSnapshotLink, type SelfPacedSnapshot } from '@/lib/selfPacedSnapshot';
import { loadProjectMilestoneComplete } from '@/lib/projectMilestone';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PendingContentItem {
  contentId: string;
  name: string;
}

export interface WeekTask {
  id: string;
  type: 'content' | 'deliverable' | 'live' | 'milestone';
  title: string;
  completed: number;
  total: number;
  isComplete: boolean;
  isActive: boolean;
  /** Workshop id (only for type=deliverable) — used to deep-link to the calendar modal */
  refId?: string;
  /** Individual pending content items (only for type=content) */
  pendingItems?: PendingContentItem[];
  /** Live session link (only for type=live) */
  liveLink?: string;
  /** Recording link (only for type=live) */
  recordingLink?: string;
  /** ISO scheduled datetime (only for type=live) */
  scheduledAt?: string;
  /** ISO fin de la sesión (only for type=live). La sesión cuenta como pasada
   *  recién después de esto, no al llegar la hora de inicio. */
  endsAt?: string;
  /** Subtype for live sessions: workshop vs mentoring */
  liveKind?: 'workshop' | 'mentoring';
  /** Milestone subtype (only for type=milestone) */
  milestoneKind?: 'project' | 'challenge_lab';
  /** (solo type=deliverable) el entregable aún NO es exigible porque la sesión en vivo
   *  del workshop todavía no ocurrió. No cuenta para el progreso y la UI lo muestra
   *  como "Disponible tras la sesión" en vez de "Pendiente de subir". */
  availableAfterSession?: boolean;
}

export interface WeeklyDeadlines {
  project_deadline?: string | null;
  project_deadline_week?: number | null;
  challenge_lab_deadline?: string | null;
  challenge_lab_deadline_week?: number | null;
}

export interface WeekChapter {
  id: string;
  name: string;
  tasks: WeekTask[];
}

export interface WeekData {
  weekNumber: number;
  title: string;
  progress: number;
  tasks: WeekTask[];
  chapters: WeekChapter[];
  isPastOrCurrent: boolean;
}

// Fin de la sesión en hora local. Sin hora de fin (o fin <= inicio) se asume
// una duración de 2h para no marcar la sesión como "finalizada" mientras dura.
function computeEndsAt(
  date: string,
  sh: number | null,
  sm: number | null,
  eh: number | null | undefined,
  em: number | null | undefined,
): string {
  const startMin = (sh ?? 0) * 60 + (sm ?? 0);
  let endMin = eh != null ? eh * 60 + (em ?? 0) : startMin + 120;
  if (endMin <= startMin) endMin = startMin + 120;
  const d = new Date(`${date}T00:00:00`);
  d.setMinutes(endMin);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

export function useWeeklyProgress(
  programId: string | undefined,
  cohortStartDate: string | undefined,
  cohortId: string | undefined,
  deadlines?: WeeklyDeadlines,
) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['weekly-progress', programId, cohortId, user?.id, deadlines?.project_deadline, deadlines?.project_deadline_week, deadlines?.challenge_lab_deadline, deadlines?.challenge_lab_deadline_week],
    queryFn: async () => {
      // 0. Load cohort_schedule_items (may be empty). We no longer require schedule
      //    items to show a week: weeks with mandatory content from program_chapters
      //    must appear even without a live session/workshop/mentoring scheduled.
      const { data: scheduleItems, error: schedErr } = await supabase
        .from('cohort_schedule_items')
        .select('id, type, ref_id, week_number, title, connection_link, meet_link, recording_link, meet_recording_link, scheduled_date, start_hour, start_minute, end_hour, end_minute')
        .eq('cohort_id', cohortId!);

      if (schedErr) throw schedErr;
      const safeScheduleItems = scheduleItems ?? [];

      // Snapshot de currículo de la cohorte (contenido obligatorio congelado al crearla).
      // Si la cohorte tiene snapshot, el conteo de obligatorios usa ESE set; si no,
      // cae al programa vivo (fallback → comportamiento anterior). Evita que agregar
      // contenido al programa des-certifique retroactivamente a cohortes en curso.
      // En rutas de autoaprendizaje (cohorte perpetua) se usa el snapshot PERSONAL.
      const { data: cohortRow } = await supabase
        .from('cohorts').select('is_perpetual').eq('id', cohortId!).maybeSingle();
      const isPerpetual = !!(cohortRow as any)?.is_perpetual;
      let personalSnap: SelfPacedSnapshot = null;
      let snapRows: any[] = [];
      if (isPerpetual) {
        const { data } = await supabase
          .from('self_paced_required_content')
          .select('content_id, is_mandatory')
          .eq('user_id', user!.id)
          .eq('cohort_id', cohortId!);
        personalSnap = data?.length ? new Map(data.map((r: any) => [r.content_id, r.is_mandatory])) : null;
      } else {
        const { data } = await supabase
          .from('cohort_required_content')
          .select('content_id')
          .eq('cohort_id', cohortId!);
        snapRows = data ?? [];
      }
      const snapshotSet = new Set(snapRows.map((r: any) => r.content_id));
      const hasSnapshot = snapshotSet.size > 0;

      // 1. Get chapters for the program with their contents (all weeks)
      const { data: allChapters, error: chapErr } = await supabase
        .from('program_chapters')
        .select(`
          id, name, week_number,
          chapter_contents ( content_id, is_mandatory )
        `)
        .eq('program_id', programId!)
        .order('week_number', { ascending: true });

      if (chapErr) throw chapErr;
      const chapters = (allChapters ?? []).filter((ch) => ch.week_number != null);


      // 2. Collect all content IDs to check progress
      const allContentIds = chapters.flatMap(
        ch => (ch.chapter_contents as any[])?.map((cc: any) => cc.content_id) ?? []
      );

      // 3. Get user content progress + content names in parallel
      const [progressRes, contentNamesRes] = allContentIds.length > 0
        ? await Promise.all([
            supabase
              .from('content_progress')
              .select('content_id, completed')
              .eq('user_id', user!.id)
              .in('content_id', allContentIds),
            supabase
              .from('content_bank')
              .select('id, name')
              .eq('planning_status', 'published')
              .in('id', allContentIds),
          ])
        : [{ data: [], error: null } as any, { data: [], error: null } as any];

      if (progressRes.error) throw progressRes.error;
      if (contentNamesRes.error) throw contentNamesRes.error;

      const completedSet = new Set(
        (progressRes.data ?? []).filter(p => p.completed).map(p => p.content_id)
      );

      const contentNameMap = new Map<string, string>();
      for (const c of contentNamesRes.data ?? []) {
        contentNameMap.set(c.id, c.name);
      }

      // 4. Workshops come from cohort_schedule_items (not program_workshops),
      //    so changes to the program after the cohort was created don't leak in.
      const workshopScheduleItems = safeScheduleItems.filter(
        (s) => s.type === 'workshop' && s.ref_id && typeof s.week_number === 'number',
      );
      const workshopRefIds = Array.from(
        new Set(workshopScheduleItems.map((s) => s.ref_id as string)),
      );

      const workshopMetaMap = new Map<string, { requires_deliverable: boolean; title: string }>();
      if (workshopRefIds.length > 0) {
        const { data: wsMeta, error: wsErr } = await supabase
          .from('program_workshops')
          .select('id, requires_deliverable, title')
          .in('id', workshopRefIds);
        if (wsErr) throw wsErr;
        for (const ws of wsMeta ?? []) {
          workshopMetaMap.set(ws.id, {
            requires_deliverable: (ws as any).requires_deliverable ?? true,
            title: (ws as any).title ?? 'Entregable',
          });
        }
      }

      const workshopsByWeek = new Map<number, {
        id: string;
        requires_deliverable: boolean;
        title: string;
        liveLink?: string;
        recordingLink?: string;
        scheduledAt?: string;
        endsAt?: string;
      }[]>();
      for (const item of workshopScheduleItems) {
        const wk = item.week_number as number;
        const refId = item.ref_id as string;
        const meta = workshopMetaMap.get(refId);
        const items = workshopsByWeek.get(wk) || [];
        const live = (item as any).connection_link || (item as any).meet_link || undefined;
        // Solo Meet nativo (Drive). Fireflies oculto en UI: si una sesion solo
        // tiene grabacion de Fireflies, queda como "Sesion finalizada" sin badge.
        const recording = (item as any).meet_recording_link || undefined;
        const date = (item as any).scheduled_date as string | null;
        const sh = (item as any).start_hour as number | null;
        const sm = (item as any).start_minute as number | null;
        let scheduledAt: string | undefined;
        let endsAt: string | undefined;
        if (date) {
          const hh = String(sh ?? 0).padStart(2, '0');
          const mm = String(sm ?? 0).padStart(2, '0');
          scheduledAt = `${date}T${hh}:${mm}:00`;
          endsAt = computeEndsAt(date, sh, sm, (item as any).end_hour, (item as any).end_minute);
        }
        items.push({
          id: refId,
          requires_deliverable: meta?.requires_deliverable ?? true,
          title: item.title || meta?.title || 'Entregable',
          liveLink: live,
          recordingLink: recording,
          scheduledAt,
          endsAt,
        });
        workshopsByWeek.set(wk, items);
      }

      const allWorkshopIds = workshopRefIds;

      // 4b. Mentorships (normal + complementary). Live-only — they do NOT count toward progress.
      const mentoringScheduleItems = safeScheduleItems.filter(
        (s) => (s.type === 'mentoring' || (s as any).type === 'mentorship') &&
               typeof s.week_number === 'number',
      );
      const mentorshipRefIds = Array.from(
        new Set(
          mentoringScheduleItems
            .map((s) => s.ref_id as string | null)
            .filter((id): id is string => !!id),
        ),
      );
      const mentorshipMetaMap = new Map<string, { title: string }>();
      if (mentorshipRefIds.length > 0) {
        const { data: msMeta, error: msErr } = await supabase
          .from('program_mentorships')
          .select('id, title')
          .in('id', mentorshipRefIds);
        if (msErr) throw msErr;
        for (const ms of msMeta ?? []) {
          mentorshipMetaMap.set((ms as any).id, { title: (ms as any).title ?? 'Mentoría' });
        }
      }
      const mentoringByWeek = new Map<number, {
        id: string;
        title: string;
        liveLink?: string;
        recordingLink?: string;
        scheduledAt?: string;
        endsAt?: string;
      }[]>();
      for (const item of mentoringScheduleItems) {
        const wk = item.week_number as number;
        const refId = (item as any).ref_id as string | null;
        const meta = refId ? mentorshipMetaMap.get(refId) : null;
        const live = (item as any).connection_link || (item as any).meet_link || undefined;
        // Solo Meet nativo (Drive). Fireflies oculto en UI: si una sesion solo
        // tiene grabacion de Fireflies, queda como "Sesion finalizada" sin badge.
        const recording = (item as any).meet_recording_link || undefined;
        const date = (item as any).scheduled_date as string | null;
        const sh = (item as any).start_hour as number | null;
        const sm = (item as any).start_minute as number | null;
        let scheduledAt: string | undefined;
        let endsAt: string | undefined;
        if (date) {
          const hh = String(sh ?? 0).padStart(2, '0');
          const mm = String(sm ?? 0).padStart(2, '0');
          scheduledAt = `${date}T${hh}:${mm}:00`;
          endsAt = computeEndsAt(date, sh, sm, (item as any).end_hour, (item as any).end_minute);
        }
        const items = mentoringByWeek.get(wk) || [];
        items.push({
          id: (item as any).id as string,
          title: (item as any).title || meta?.title || 'Mentoría',
          liveLink: live,
          recordingLink: recording,
          scheduledAt,
          endsAt,
        });
        mentoringByWeek.set(wk, items);
      }


      // 5. Get user deliverables by workshop_id
      // Ojo: workshop_id es uuid. Filtrar con un placeholder tipo '__none__' provoca
      // un 400 en PostgREST y rompe todo el hook (rutas de autoaprendizaje no tienen
      // workshops). Si no hay ids, simplemente no consultamos.
      let deliverables: { workshop_id: string | null; status: string | null }[] = [];
      if (allWorkshopIds.length > 0) {
        const { data: delData, error: delErr } = await supabase
          .from('participant_deliverables')
          .select('workshop_id, status')
          .eq('user_id', user!.id)
          .in('workshop_id', allWorkshopIds);
        if (delErr) throw delErr;
        deliverables = (delData ?? []) as any;
      }

      const submittedWorkshopIds = new Set<string>();
      for (const d of deliverables ?? []) {
        if (d.workshop_id && (d.status === 'submitted' || d.status === 'validated')) {
          submittedWorkshopIds.add(d.workshop_id);
        }
      }

      // 5b. Milestone resolution (project deadline + challenge lab deadline)
      const weekFromDate = (iso: string | null | undefined): number | null => {
        if (!iso || !cohortStartDate) return null;
        const ymd = cohortStartDate.slice(0, 10).split('-');
        const start = ymd.length === 3
          ? new Date(Number(ymd[0]), Number(ymd[1]) - 1, Number(ymd[2]))
          : new Date(cohortStartDate);
        const target = new Date(iso);
        const startLocal = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const targetLocal = new Date(target.getFullYear(), target.getMonth(), target.getDate());
        const diffDays = Math.floor((targetLocal.getTime() - startLocal.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) return null;
        return Math.floor(diffDays / 7) + 1;
      };

      const projectWeek = deadlines?.project_deadline_week ?? weekFromDate(deadlines?.project_deadline);
      const challengeWeek = deadlines?.challenge_lab_deadline_week ?? weekFromDate(deadlines?.challenge_lab_deadline);

      let projectMilestoneComplete = false;
      let challengeMilestoneComplete = false;

      if (projectWeek != null) {
        if (!user || !cohortId) throw new Error('Missing participant or cohort for project progress');
        projectMilestoneComplete = await loadProjectMilestoneComplete(user.id, cohortId);
      }

      if (challengeWeek != null) {
        // challenges table has no cohort_id; scope by current user (who belongs to this cohort)
        const { data: chs } = await (supabase as any)
          .from('challenges')
          .select('id')
          .eq('user_id', user!.id);
        const chIds = (chs ?? []).map((c: any) => c.id);
        if (chIds.length > 0) {
          // Alinear con el widget "Pendientes" (challengeLabCompleted en ParticipantDashboard):
          // challenge_solutions NO tiene columna user_id; el usuario ya queda acotado porque
          // chIds proviene de challenges filtradas por user_id. Contar solo por challenge_id.
          const { count } = await (supabase as any)
            .from('challenge_solutions')
            .select('id', { count: 'exact', head: true })
            .in('challenge_id', chIds);
          challengeMilestoneComplete = (count ?? 0) > 0;
        }
      }

      // 6. Build week data — aggregate ALL chapters by week_number
      const currentWeek = getCurrentWeek(cohortStartDate);

      type WeekAgg = {
        chapterNames: string[];
        chapterIds: string[];
        mandatoryContents: { content_id: string }[];
        isActiveWeek: boolean;
      };

      const weekAgg = new Map<number, WeekAgg>();
      for (const ch of chapters) {
        const weekNum = ch.week_number ?? 0;
        const isActiveWeek = weekNum <= currentWeek;
        const contents = (ch.chapter_contents as any[]) ?? [];
        const mandatory = contents
          .filter((cc: any) => isPerpetual
            ? (() => { const r = resolveSnapshotLink(personalSnap, cc.content_id, cc.is_mandatory !== false); return r.counts && r.mandatory; })()
            : cc.is_mandatory !== false)
          .map((cc: any) => ({ content_id: cc.content_id }));

        const existing = weekAgg.get(weekNum);
        if (existing) {
          existing.chapterNames.push(ch.name);
          existing.chapterIds.push(ch.id);
          existing.mandatoryContents.push(...mandatory);
        } else {
          weekAgg.set(weekNum, {
            chapterNames: [ch.name],
            chapterIds: [ch.id],
            mandatoryContents: mandatory,
            isActiveWeek,
          });
        }
      }

      // Include weeks that have a workshop/entregable but no chapter
      for (const weekNum of workshopsByWeek.keys()) {
        if (!weekAgg.has(weekNum)) {
          weekAgg.set(weekNum, {
            chapterNames: [],
            chapterIds: [],
            mandatoryContents: [],
            isActiveWeek: weekNum <= currentWeek,
          });
        }
      }

      // Include weeks that only have a mentoring session
      for (const weekNum of mentoringByWeek.keys()) {
        if (!weekAgg.has(weekNum)) {
          weekAgg.set(weekNum, {
            chapterNames: [],
            chapterIds: [],
            mandatoryContents: [],
            isActiveWeek: weekNum <= currentWeek,
          });
        }
      }

      // Include weeks that only host a milestone (project / challenge_lab deadline)
      for (const weekNum of [projectWeek, challengeWeek]) {
        if (weekNum != null && !weekAgg.has(weekNum)) {
          weekAgg.set(weekNum, {
            chapterNames: [],
            chapterIds: [],
            mandatoryContents: [],
            isActiveWeek: weekNum <= currentWeek,
          });
        }
      }



      const weeks: WeekData[] = Array.from(weekAgg.entries())
        .sort(([a], [b]) => a - b)
        .map(([weekNumber, agg]) => {
          // Dedupe content ids in case a content appears in multiple chapters.
          // Si hay snapshot de la cohorte, solo cuentan los content_id congelados
          // (contenido nuevo agregado al programa después no cuenta).
          const uniqueContentIds = Array.from(new Set(agg.mandatoryContents.map(m => m.content_id)))
            .filter(id => !hasSnapshot || snapshotSet.has(id));
          const totalContent = uniqueContentIds.length;
          const completedContent = uniqueContentIds.filter(id => completedSet.has(id)).length;
          const contentComplete = totalContent > 0 && completedContent === totalContent;
          const pendingItems: PendingContentItem[] = uniqueContentIds
            .filter(id => !completedSet.has(id))
            .map(id => ({
              contentId: id,
              name: contentNameMap.get(id) || 'Contenido sin nombre',
            }));

          const weekWorkshops = workshopsByWeek.get(weekNumber) ?? [];
          const weekWorkshopIds = weekWorkshops.map(w => w.id);
          const needsDeliverable = weekWorkshops.some(w => w.requires_deliverable);
          const hasDeliverable = weekWorkshopIds.some(id => submittedWorkshopIds.has(id));

          const tasks: WeekTask[] = [];
          if (totalContent > 0) {
            tasks.push({
              id: `week-${weekNumber}-content`,
              type: 'content',
              title: 'Contenido obligatorio',
              completed: completedContent,
              total: totalContent,
              isComplete: contentComplete,
              isActive: agg.isActiveWeek,
              pendingItems,
            });
          }
          if (needsDeliverable) {
            const deliverableWorkshop = weekWorkshops.find(w => w.requires_deliverable);
            // El entregable no debe figurar como "pendiente" hasta DESPUÉS de la sesión
            // en vivo del workshop (evita confundir al participante al inicio de la semana,
            // cuando la sesión aún no se dictó). Si el workshop no tiene fecha de sesión,
            // no se bloquea (se comporta como antes).
            const sessionAt = deliverableWorkshop?.scheduledAt;
            const sessionOccurred = sessionAt ? new Date(sessionAt).getTime() < Date.now() : true;
            const availableAfterSession = !hasDeliverable && !sessionOccurred;
            tasks.push({
              id: `week-${weekNumber}-deliverable`,
              type: 'deliverable',
              title: deliverableWorkshop?.title || 'Entregable de la semana',
              completed: hasDeliverable ? 1 : 0,
              total: 1,
              isComplete: hasDeliverable,
              isActive: agg.isActiveWeek,
              refId: deliverableWorkshop?.id,
              availableAfterSession,
            });
          }

          if (projectWeek === weekNumber) {
            tasks.push({
              id: `week-${weekNumber}-milestone-project`,
              type: 'milestone',
              title: 'Entrega del proyecto',
              completed: projectMilestoneComplete ? 1 : 0,
              total: 1,
              isComplete: projectMilestoneComplete,
              isActive: agg.isActiveWeek,
              milestoneKind: 'project',
            });
          }
          if (challengeWeek === weekNumber) {
            tasks.push({
              id: `week-${weekNumber}-milestone-challenge`,
              type: 'milestone',
              title: 'Laboratorio reto y solución',
              completed: challengeMilestoneComplete ? 1 : 0,
              total: 1,
              isComplete: challengeMilestoneComplete,
              isActive: agg.isActiveWeek,
              milestoneKind: 'challenge_lab',
            });
          }


          // Live session tasks (one per workshop) — do NOT count toward progress
          for (const ws of weekWorkshops) {
            const past = ws.endsAt ? new Date(ws.endsAt).getTime() < Date.now() : false;
            const isComplete = !!ws.recordingLink || (past && !ws.liveLink);
            tasks.push({
              id: `week-${weekNumber}-live-${ws.id}`,
              type: 'live',
              title: ws.title || 'Sesión en vivo',
              completed: isComplete ? 1 : 0,
              total: 1,
              isComplete,
              isActive: true,
              liveLink: ws.liveLink,
              recordingLink: ws.recordingLink,
              scheduledAt: ws.scheduledAt,
              endsAt: ws.endsAt,
              liveKind: 'workshop',
            });
          }

          // Mentoring live tasks (normal + complementary) — do NOT count toward progress
          const weekMentorings = mentoringByWeek.get(weekNumber) ?? [];
          for (const ms of weekMentorings) {
            const past = ms.endsAt ? new Date(ms.endsAt).getTime() < Date.now() : false;
            const isComplete = !!ms.recordingLink || (past && !ms.liveLink);
            tasks.push({
              id: `week-${weekNumber}-mentoring-${ms.id}`,
              type: 'live',
              title: ms.title || 'Mentoría',
              completed: isComplete ? 1 : 0,
              total: 1,
              isComplete,
              isActive: true,
              liveLink: ms.liveLink,
              recordingLink: ms.recordingLink,
              scheduledAt: ms.scheduledAt,
              endsAt: ms.endsAt,
              liveKind: 'mentoring',
            });
          }


          // Cálculo proporcional: la semana suma TODAS sus unidades exigibles
          // (contenido obligatorio + entregable + hitos), activa o futura.
          // Así, ver solo el contenido nunca muestra 100% si la semana también
          // requiere entregable o hito: 7 de 8 unidades = 88%.
          let totalUnits = 0;
          let doneUnits = 0;
          for (const t of tasks) {
            if (t.type === 'live') continue;
            totalUnits += t.total;
            doneUnits += t.completed;
          }
          let progress = totalUnits > 0 ? Math.round((doneUnits / totalUnits) * 100) : 0;
          // Si la semana no tiene tareas contables (solo live o nada) y ya pasó,
          // se considera 100% para no dejarla clavada en 0% para siempre.
          if (totalUnits === 0 && weekNumber < currentWeek) {
            progress = 100;
          }

          return {
            weekNumber,
            title: weekWorkshops[0]?.title
              || weekMentorings[0]?.title
              || '',

            progress,
            tasks,
            chapters: agg.chapterIds.map((id, i) => ({ id, name: agg.chapterNames[i], tasks: [] })),
            isPastOrCurrent: agg.isActiveWeek,
          };
        })
        .filter((w) => w.tasks.length > 0);

      return weeks;

    },
    enabled: !!programId && !!cohortId && !!user?.id,
  });
}

export function getCurrentWeek(cohortStartDate: string | undefined): number {
  if (!cohortStartDate) return 1;
  // Parse "yyyy-MM-dd" as LOCAL calendar date to avoid UTC timezone shifts.
  const ymd = cohortStartDate.slice(0, 10).split('-');
  const start = ymd.length === 3
    ? new Date(Number(ymd[0]), Number(ymd[1]) - 1, Number(ymd[2]))
    : new Date(cohortStartDate);
  const now = new Date();
  const startLocal = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const nowLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.floor((nowLocal.getTime() - startLocal.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 1;
  return Math.floor(diffDays / 7) + 1;
}
